import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Lightbulb, Send } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useSuggestionsStore, PartnerType } from "@/data/suggestionsStore";

interface PartnerSuggestionPanelProps {
  partnerType: PartnerType;
  partnerName: string;
}

export default function PartnerSuggestionPanel({ partnerType, partnerName }: PartnerSuggestionPanelProps) {
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { addSuggestion } = useSuggestionsStore();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !text.trim()) {
      toast({ title: "Champs requis", description: "Veuillez remplir l'objet et le texte de la suggestion.", variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    addSuggestion({
      partnerType,
      partnerName,
      subject,
      text
    });

    setTimeout(() => {
      setSubject("");
      setText("");
      setIsSubmitting(false);
      toast({ title: "Suggestion envoyée", description: "Merci de contribuer à l'amélioration de Kayn'in ! Votre suggestion a été transmise à l'administration." });
    }, 600);
  };

  return (
    <Card className="border-border shadow-sm border-t-4 border-t-primary">
      <CardHeader className="bg-primary/5 pb-4">
        <CardTitle className="text-xl flex items-center gap-2 font-display text-primary">
          <Lightbulb className="w-6 h-6" />
          Boîte à propositions
        </CardTitle>
        <CardDescription className="text-foreground pt-2">
          Kayn'in a pour mission de moderniser l'engagement démocratique de manière innovante, neutre et objective. Pour continuer à faire évoluer cette initiative citoyenne, votre avis compte. Partagez-nous vos suggestions d'amélioration !
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-semibold text-muted-foreground">Objet de la suggestion</label>
            <Input 
              placeholder="Ex: Nouvelle fonctionnalité pour les débats..." 
              value={subject} 
              onChange={e => setSubject(e.target.value)} 
              className="bg-background"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-semibold text-muted-foreground">Détail de votre proposition</label>
            <Textarea 
              placeholder="Décrivez votre idée, les problèmes rencontrés ou les améliorations souhaitées..." 
              value={text} 
              onChange={e => setText(e.target.value)} 
              className="min-h-[150px] bg-background resize-y"
            />
          </div>
          <div className="flex justify-end pt-2">
            <Button type="submit" disabled={isSubmitting} className="gap-2 px-8">
              {isSubmitting ? <span className="animate-spin">⟳</span> : <Send className="w-4 h-4" />}
              Soumettre la proposition
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
